import type {
  CreateKlarnaOrderPayload,
  KlarnaOrder,
  KlarnaOrderLine,
  ShippingOption
} from 'ecosystem'
import { klarnaApiCall, klarnaOrderLineFactory } from 'api'
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, resolveUrlPath, sumArrayOfNumbers } from 'shared-utils'
import { runCreateOrder } from './runCreateOrder'
import {
  type BuildPayloadArgs,
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

/**
 * `availableCountries` is the only gate on the shopper's pick. Which
 * country/currency combinations are actually permitted is set on the
 * merchant's Kustom contract, not by a fixed pairing — selling to Poland in SEK
 * works when the account covers it, it just means Kustom's Polish local payment
 * methods are not offered.
 * @see https://docs.kustom.co/contents/checkout/use-cases/selling-to-multiple-countries
 */
const buildKlarnaPayload = ({
  ctx,
  opts,
  backendOrder,
  sessionCountry,
  configCountry,
  shippingInfo
}: BuildPayloadArgs): CreateKlarnaOrderPayload => {
  const klarnaOptions = opts.paymentProviderConfig?.providerOptions?.klarna
  const { items, cartDiscountCode } = ctx.payload
  const { orderLines, orderAmount } = klarnaOrderLineFactory(
    items,
    cartDiscountCode,
    configCountry,
    opts.buyerVat
  )
  const taxAmount: number = sumArrayOfNumbers(
    orderLines.map((item: KlarnaOrderLine) => item.total_tax_amount)
  )

  const base = {
    locale: ctx.locale,
    purchase_country: sessionCountry || configCountry || DEFAULT_COUNTRY,
    purchase_currency: klarnaOptions?.purchaseCurrency || DEFAULT_CURRENCY,
    order_amount: orderAmount,
    order_tax_amount: taxAmount,
    order_lines: orderLines,
    merchant_reference1: backendOrder.orderNumber,
    merchant_reference2: '',
    merchant_data: '',
    merchant_urls: {
      terms: resolveUrlPath(opts.redirectDomain, klarnaOptions?.termsUrl ?? '/terms'),
      checkout: resolveUrlPath(opts.redirectDomain, '/checkout?order_id={checkout.order.id}'),
      confirmation: resolveUrlPath(
        opts.redirectDomain,
        '/confirmation?order_id={checkout.order.id}'
      ),
      push: resolveUrlPath(opts.redirectDomain, '/api/orders/push?order_id={checkout.order.id}'),
      ...(klarnaOptions?.cancellationTermsUrl
        ? {
            cancellation_terms: resolveUrlPath(
              opts.redirectDomain,
              klarnaOptions.cancellationTermsUrl
            )
          }
        : {})
    },
    options: {
      phone_mandatory: klarnaOptions?.phoneMandatory ?? true,
      // Only the carrier-less flow lets the shopper split the two addresses;
      // with a carrier the delivery address belongs to the carrier's own step.
      allow_separate_shipping_address: !!shippingInfo,
      color_button: klarnaOptions?.colorButton || opts.color,
      color_checkbox: opts.color,
      color_checkbox_checkmark: opts.color,
      color_header: opts.color,
      color_link: opts.color,
      radius_border: 'sm'
    }
  } as CreateKlarnaOrderPayload

  if (!shippingInfo) {
    return base
  }

  // Klarna adds the selected `shipping_options` entry on top of `order_lines`.
  // That is right for the legacy single flat rate, which nothing else ever
  // touches — but a store with configured delivery options has an FE delivery
  // step that pushes its own `shipping_fee` line on every change, so leaving a
  // shipping option in place would charge the shopper freight twice. There,
  // freight goes in the lines from the start and Klarna renders no selector of
  // its own.
  return opts.shippingProviderConfig?.defaultOptions?.methods?.length
    ? withFreightLine(base, shippingInfo)
    : { ...base, shipping_options: [shippingInfo] }
}

const withFreightLine = (
  payload: CreateKlarnaOrderPayload,
  shippingInfo: ShippingOption
): CreateKlarnaOrderPayload => ({
  ...payload,
  order_lines: [
    ...payload.order_lines,
    {
      type: 'shipping_fee',
      name: shippingInfo.name,
      quantity: 1,
      unit_price: shippingInfo.price,
      total_amount: shippingInfo.price,
      tax_rate: shippingInfo.tax_rate,
      total_tax_amount: shippingInfo.tax_amount,
      total_discount_amount: 0,
      merchant_data: ''
    }
  ],
  order_amount: payload.order_amount + shippingInfo.price,
  order_tax_amount: payload.order_tax_amount + shippingInfo.tax_amount
})

const klarnaAdapter: PaymentProviderAdapter<KlarnaOrder, CreateKlarnaOrderPayload> = {
  name: 'KLARNA',
  configCountry: (opts) => opts.paymentProviderConfig?.providerOptions?.klarna?.purchaseCountry,
  availableCountries: (opts) =>
    opts.paymentProviderConfig?.providerOptions?.klarna?.availableCountries,
  orderLineFactory: klarnaOrderLineFactory,
  buildPayload: buildKlarnaPayload,
  createSession: (payload) =>
    klarnaApiCall<KlarnaOrder, CreateKlarnaOrderPayload>('create', { payload }),
  respond: (klarnaResponse) => ({ klarnaResponse })
}

export const klarnaPaymentProvider = (
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts
): Promise<CreateOrderRouteResponse> => runCreateOrder(ctx, opts, klarnaAdapter)
